import type { UpdateUserDto } from "@/dtos/UpdateUserDto";
import type { CreateUserDto } from "@/dtos/CreateUserDto";
import api from "@/services/api";
import { useMutation, useQuery } from "@tanstack/react-query";

interface GetUsersParams {
  role?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const useGetUsers = (params?: GetUsersParams) => {
  const { role, search, page = 1, limit = 10 } = params || {};

  return useQuery({
    queryKey: ["getUsers", role, search, page, limit],
    queryFn: async () => {
      const response = await api.get("/accounts/search-users", {
        params: {
          ...(role && { role }),
          ...(search && { search }),
          page,
          limit,
        },
      });
      return response.data;
    },
  });
};

export const useCreateUser = () => {
  return useMutation({
    mutationKey: ["create-user"],
    mutationFn: async (createUserDto: CreateUserDto) => {
      const response = await api.post("/users", createUserDto);
      return response.data;
    },
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationKey: ["update-user"],
    mutationFn: async ({
      userId,
      ...updateUserDto
    }: UpdateUserDto & { userId: string }) => {
      const response = await api.patch(`/users/${userId}`, updateUserDto);
      return response.data;
    },
  });
};

export const useDeleteUser = () => {
  return useMutation({
    mutationKey: ["delete-user"],
    mutationFn: async (userId: string) => {
      const response = await api.delete(`/users/${userId}`);
      return response.data;
    },
  });
};

export const useGetUserById = (id: string) => {
  return useQuery({
    queryKey: ["getUserById", id],
    queryFn: async () => {
      const response = await api.get(`/users/${id}`);
      return response.data;
    },
  });
};
